import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Stack, TablePagination } from "@mui/material";
import { useGetMany } from "react-admin";
import type { LoadOperation } from "@/types";
import { ENTITY_CONFIG, GroupedEntityIds } from "./config";
import type { RowAttention } from "./loadResultModel";
import { useClaimantLookup } from "./hooks";
import { LoadedRecordRow } from "./LoadedRecordRow";

/** Default per-page when a schema config doesn't specify one. Pagination is
 *  only rendered when `totalCount > pageSize`, so this default doesn't
 *  produce a "1 of 1" footer on small loads. */
const DEFAULT_PAGE_SIZE = 10;

interface LoadedRecordsListProps extends GroupedEntityIds {
  /** Load warnings by claimant id; a row matches on its own id or its claimant_id_ref. */
  attention?: Record<string, RowAttention>;
}

/**
 * Renders the loaded records for a single target schema as a vertical list of
 * `LoadedRecordRow`s. Replaces the legacy `EntitySection`'s
 * `PaginatedDatagrid` — the chunky multi-column grid that motivated the
 * redesign.
 *
 * Pagination is rendered only when total records exceed the schema's
 * configured page size (defaults to 10). At or below the threshold, the list
 * just renders all rows — no "1 of 1" footer.
 *
 * Fetches the current page's entities via `useGetMany`; only the visible
 * slice is fetched per page change.
 */
export const LoadedRecordsList = ({ targetSchema, entityIds, operations, attention }: LoadedRecordsListProps) => {
  const config = ENTITY_CONFIG[targetSchema];
  const totalCount = entityIds.length;
  const pageSize = config.defaultPerPage ?? DEFAULT_PAGE_SIZE;
  const showPagination = totalCount > pageSize;

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(pageSize);

  // Reset to page 0 when the record set changes size. Without this, polling
  // (or a re-run of the load) that shrinks the count below `page * perPage`
  // leaves `paginatedIds` empty, `useGetMany` disabled, and the list
  // rendering blank with no affordance to recover.
  useEffect(() => {
    setPage(0);
  }, [totalCount]);

  const paginatedIds = useMemo(() => {
    if (!showPagination) return entityIds;
    const start = page * perPage;
    return entityIds.slice(start, start + perPage);
  }, [entityIds, page, perPage, showPagination]);

  const { data: entities, isLoading } = useGetMany<{ id: string } & Record<string, unknown>>(
    config.resource,
    { ids: paginatedIds },
    { enabled: paginatedIds.length > 0 }
  );

  // Build id→entity map for quick lookup, including missing IDs as null
  // entries so the row renders the "Record not found" treatment.
  const rowData = useMemo<LoadedRecordRowData[]>(() => {
    const byId = new Map<string, Record<string, unknown>>();
    (entities ?? []).forEach((e) => byId.set(e.id, e));

    return paginatedIds
      .map<LoadedRecordRowData>((id) => {
        const entity = byId.get(id) ?? null;
        return { id, entity, operation: operations[id], isMissing: entity === null };
      })
      .sort((a, b) => {
        // Group rows by claimant_id_ref where applicable (matches the legacy
        // EntitySection's sort) so multiple reimbursements/claims for the
        // same claimant appear adjacent.
        const aRef = (a.entity?.claimant_id_ref as string) ?? "";
        const bRef = (b.entity?.claimant_id_ref as string) ?? "";
        return aRef.localeCompare(bRef);
      });
  }, [paginatedIds, entities, operations]);

  const claimantLookup = useClaimantLookup(
    rowData.map((r) => r.entity).filter((e): e is Record<string, unknown> => e !== null)
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack sx={{ borderTop: 1, borderColor: "divider" }}>
        {rowData.map((row) => (
          <LoadedRecordRow
            key={row.id}
            entity={row.entity}
            operation={row.operation}
            fields={config.fields}
            claimantLookup={claimantLookup}
            to={row.isMissing ? undefined : `/${config.resource}/${row.id}/show`}
            isMissing={row.isMissing}
            attention={attention?.[row.id] ?? attention?.[String(row.entity?.claimant_id_ref ?? "")]}
          />
        ))}
      </Stack>
      {showPagination && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => {
            setPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </Box>
  );
};

interface LoadedRecordRowData {
  id: string;
  entity: Record<string, unknown> | null;
  operation: LoadOperation;
  isMissing: boolean;
}
